/**
 * AuditEventsView -- admin audit log browser (P39.1).
 *
 * Route: /admin/audit-events
 *
 * Surfaces the P4.3 GET /v1/admin/audit/events backend behind a
 * filter form + paginated table + per-row payload viewer +
 * click-through to the source entity where one exists. Admin-only.
 *
 * Default view: last 7 days, 25 rows per page, newest-first.
 * System events (system.cmdb.* / system.mvb.*) included by
 * default; operator filters them out via
 * event_type_prefix=admin. when noise-hiding is wanted.
 */
import { Link } from "react-router-dom";
import { Bdi } from "shared/components/Bdi";
import { entityDetailPath } from "shared/routes/entityRoutes";
import { Figure } from "shared/components/Figure";
import { formatDateTime } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useEffect } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { entityLabelFromRows } from "shared/admin/auditEntityLabel";
import { useNavigate, useSearchParams } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import { useAuditEventTypes, useAuditEvents } from "shared/api/hooks/useAuditEvents";
import type { AuditEvent } from "shared/api/types";

import type { TFunction } from "i18next";

import { Identifier } from "shared/components/Identifier";
import { said } from "shared/i18n/said";
import { entityKindLabel } from "shared/vocab/entityKind";
import { auditEventWords } from "shared/admin/auditEventWords";
const DEFAULT_LIMIT = 25;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/** Round 19 I24 -- a kind in words: "ibs group", "ibs service" and
 *  "framework assessment" reached the list as de-underscored codes. */
function kindWords(t: TFunction, kind: string | null | undefined): string {
  if (!kind) return "";
  return said(t, `screens.auditEventsView.kind.${kind}`, entityKindLabel(kind));
}

const ENTITY_TYPE_OPTIONS = [
  "",
  "service",
  "ibs_service",
  "mvb_declaration",
  "connector",
  "twin_analyser_run",
  "framework_assessment",
  "framework_analyser_run",
  "framework_cycle",
  "chain",
  // Round 17 H35 -- the kinds the trail carries that the filter could
  // not select.
  "service_account",
  "app_setting",
  "regulator_letter",
  "cap",
  "attestation",
  "deployment",
] as const;

function isoNow(): string {
  return new Date().toISOString();
}

function isoSevenDaysAgo(): string {
  return new Date(Date.now() - SEVEN_DAYS_MS).toISOString();
}

// Targets for the "View source" click-through. Best-effort: a
// missing route is a 404 the operator can route around. Better
// than nothing.
export function clickThroughTarget(event: AuditEvent): string | null {
  // Round 17 H35 -- a restore carries no record id; its source is the
  // restore record on Settings, beside the stated one.
  if (event.entity_type === "deployment") return "/admin/settings#restore";
  if (!event.entity_type || !event.entity_id) return null;
  /* Round 14 G, sixth round -- "View source" landed on the REGISTER:
     the connector grid with nothing opened, the service register with
     no drawer. Both registers already open a record named in the URL
     (`?connector=` since round 10 D3, `?edit=` since P15), so the row
     now names its record. A run is shown in its parent's row; the
     server says which parent (`source_id`), because a run's own id
     opens nothing. */
  const id = encodeURIComponent(event.entity_id);
  const source = event.source_id ? encodeURIComponent(event.source_id) : null;
  switch (event.entity_type) {
    case "service":
    case "services":
      return `/foundation/admin/services?edit=${id}`;
    case "ibs_service":
    case "ibs":
      return `/foundation/admin/ibses?edit=${id}`;
    case "vendor":
      return `/foundation/admin/vendors?edit=${id}`;
    case "mvb_declaration":
      return `/foundation/mvb/${event.entity_id}`;
    case "connector":
      return `/admin/connectors?connector=${id}`;
    case "connector_run":
      return source ? `/admin/connectors?connector=${source}` : "/admin/connectors";
    case "twin_analyser_run":
      return source
        ? `/foundation/admin/services?edit=${source}`
        : `/foundation/admin/services`;
    case "finding":
    case "findings":
      return `/twin/findings/${event.entity_id}`;
    case "import_batch": {
      /* Round 16 F8 -- since E9 a commit's row names the batch, not the
         record, and nothing on it led to what it wrote. The batch's own
         review carries "See the rows you imported"; a staging, or a
         commit that wrote nothing, has nothing to open. */
      const p = (event.payload ?? {}) as { inserted?: number; updated?: number };
      const wrote = (p.inserted ?? 0) + (p.updated ?? 0);
      return event.event_type.endsWith(".commit") && wrote > 0
        ? `/admin/data-import?batch=${id}`
        : null;
    }
    case "chain":
      // A chain slug, not a row: the trail IS the destination, so this
      // one legitimately re-filters this page. It is spelled as a
      // prefix filter rather than an entity, which is why arriving
      // here changes what is shown.
      return `/admin/audit-events?event_type_prefix=admin.foundation.mvb.recompute`;
    default:
      return null;
  }
}

export function AuditEventsView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;

  /*
   * #581: seed filter state from the URL query params.
   *
   * Round 9 E1 -- and RE-seed when they change. This read them on
   * mount alone, so any navigation to this same route left the form
   * exactly as it was: the URL said one thing and the table showed
   * another, silently. A link into a filtered audit trail is the only
   * reason these params exist.
   *
   * The form still owns the state from then on -- it does not push
   * back to the URL, so `searchParams` only moves when somebody
   * navigates, which is precisely when the form should follow.
   */
  const [searchParams] = useSearchParams();

  const [eventTypePrefix, setEventTypePrefix] = useState<string>(
    () => searchParams.get("event_type_prefix") ?? "",
  );
  const [entityType, setEntityType] = useState<string>(
    () => searchParams.get("entity_type") ?? "",
  );
  const [entityId, setEntityId] = useState<string>(
    () => searchParams.get("entity_id") ?? "",
  );
  const [since, setSince] = useState<string>(
    () => searchParams.get("since") ?? isoSevenDaysAgo(),
  );
  const [until, setUntil] = useState<string>(
    () => searchParams.get("until") ?? isoNow(),
  );
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [offset, setOffset] = useState<number>(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The re-seed the comment above describes. Keyed on the raw query
  // string so it fires once per navigation, not once per render.
  const search = searchParams.toString();
  useEffect(() => {
    const p = new URLSearchParams(search);
    if (!p.has("entity_type") && !p.has("entity_id") && !p.has("event_type_prefix")) {
      // A bare /admin/audit-events is "show me everything", not "clear
      // what I just typed".
      return;
    }
    setEventTypePrefix(p.get("event_type_prefix") ?? "");
    setEntityType(p.get("entity_type") ?? "");
    setEntityId(p.get("entity_id") ?? "");
    setOffset(0);
  }, [search]);

  const navigate = useNavigate();

  const query = useMemo(
    () => ({
      ...(eventTypePrefix ? { event_type_prefix: eventTypePrefix } : {}),
      ...(entityType ? { entity_type: entityType } : {}),
      ...(entityId ? { entity_id: entityId } : {}),
      ...(since ? { since } : {}),
      ...(until ? { until } : {}),
      limit,
      offset,
    }),
    [eventTypePrefix, entityType, entityId, since, until, limit, offset],
  );

  const events = useAuditEvents(query, { enabled: isAdmin });
  const eventTypes = useAuditEventTypes();

  if (!isAdmin) {
    return <AdminOnlyPanel surface="The audit log" />;
  }

  const rows = events.data?.events ?? [];
  // The audit table stores entity_type + entity_id and no name, so the
  // kind is read off the rows the filter returned.
  const filteredKind =
    entityType || rows.find((r) => r.entity_id === entityId)?.entity_type || null;
  // D3 -- the product wrote these rows; what it wrote about the
  // subject names it. Falls back to the id, which is worse to read and
  // still correct.
  // D1 (round 8) -- the server names the row now, by reading the row
  // the audit points AT. The payload reader stays as a fallback for the
  // kinds the server map does not cover, so a name that used to appear
  // does not stop appearing.
  const filteredName = entityId
    ? (rows.find((r) => r.entity_id === entityId)?.entity_label ??
      entityLabelFromRows(rows, entityId))
    : null;
  const filteredHref =
    filteredKind && entityId ? entityDetailPath(filteredKind, entityId) : null;
  const total = events.data?.total ?? 0;
  const hasNext = offset + limit < total;
  const hasPrev = offset > 0;

  return (
    <div data-testid="audit-events-view" className="flex flex-col gap-4 p-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="hash-chain"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.auditEventsView.auditEvents")}
        </h1>
        <p className="text-xs text-slate-600">
          {t("screens.auditEventsView.everyAdminActionSystemDerivedWriteThe")}
        </p>
      </header>

      {entityId && (
        <div
          data-testid="audit-events-entity-id-badge"
          className="flex items-center gap-2 rounded border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs text-indigo-900"
        >
          <span className="font-semibold">
            {t("screens.auditEventsView.filteredByEntity")}
          </span>
          {/* H4 -- this was a bare UUID and nothing else, so the banner
              said "you are filtered" without saying to WHAT. The kind
              comes off the rows (the audit table stores no name), and
              the link goes to the entity itself, which answers the
              question better than a name would. */}
          {filteredKind && (
            <span
              data-testid="audit-events-entity-kind"
              className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700"
            >
              {kindWords(t, filteredKind)}
            </span>
          )}
          {filteredHref ? (
            <Link
              to={filteredHref}
              data-testid="audit-events-entity-id-value"
              className={
                filteredName
                  ? "text-brand-brassDark hover:underline"
                  : "font-mono text-brand-brassDark hover:underline"
              }
              title={filteredName ? entityId : undefined}
              dir="auto"
            >
              {filteredName ?? entityId}
            </Link>
          ) : (
            <span
              data-testid="audit-events-entity-id-value"
              className={filteredName ? undefined : "font-mono"}
              title={filteredName ? entityId : undefined}
              dir="auto"
            >
              {filteredName ?? entityId}
            </span>
          )}
          <button
            type="button"
            data-testid="audit-events-entity-id-clear"
            onClick={() => {
              setEntityId("");
              setOffset(0);
            }}
            className="ms-auto rounded border border-indigo-400 bg-white px-2 py-0.5 text-[10px] font-semibold text-indigo-900"
          >
            {t("screens.auditEventsView.clear")}
          </button>
        </div>
      )}

      {/* ROUND 15 E9 -- the importer writes one audit event per batch,
          so "which import touched this record" is answered from the
          batch's own rows rather than from a row per record here. */}
      {entityId && (events.data?.imports?.length ?? 0) > 0 && (
        <ul
          data-testid="audit-events-imports"
          className="space-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
        >
          {(events.data?.imports ?? []).map((imp) => (
            <li
              key={`${imp.batch_id}-${imp.row_number}`}
              className="flex flex-wrap gap-x-2"
            >
              <span>
                {t(
                  imp.action === "update"
                    ? "screens.auditEventsView.importTouchUpdate"
                    : "screens.auditEventsView.importTouchInsert",
                  {
                    filename: imp.filename ?? t("screens.auditEventsView.unnamedFile"),
                    row: imp.row_number,
                    when: imp.committed_at ? formatDateTime(imp.committed_at) : "",
                  },
                )}
                {imp.status === "undone" && (
                  <> {t("screens.auditEventsView.importTouchUndone")}</>
                )}
              </span>
              <Link
                to={`/admin/data-import?batch=${encodeURIComponent(imp.batch_id)}`}
                data-testid="audit-events-import-batch-link"
                className="text-brand-brassDark hover:underline"
              >
                {t("screens.auditEventsView.openTheBatch")}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* Filter form */}
      <fieldset
        data-testid="audit-events-filters"
        className="grid grid-cols-1 gap-2 rounded border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-3"
      >
        <label className="block text-xs">
          <span className="text-slate-600">
            {t("screens.auditEventsView.eventTypePrefix")}
          </span>
          {/* H12: a free-text box with an example in the placeholder.
              An admin typed "admin.settings", got nothing, and had no
              way to learn the real prefix is "admin.foundation". The
              list is DERIVED from what the log holds, so it cannot
              offer a type that is not there. */}
          <input
            dir="auto"
            data-testid="audit-events-filter-prefix"
            type="text"
            list="audit-event-types"
            // C4 -- the placeholder taught a hard-coded pattern that
            // returns nothing on an estate whose prefixes differ, while
            // the datalist holding the real answer grew to 69 entries
            // without becoming reachable. The example is taken from
            // what the log actually holds, so it cannot teach a prefix
            // that is not there.
            placeholder={
              eventTypes.data?.[0]?.event_type
                ? t("screens.auditEventsView.prefixExample", {
                    example: eventTypes.data[0].event_type,
                  })
                : t("screens.auditEventsView.eGAdminFoundationOr")
            }
            value={eventTypePrefix}
            onChange={(e) => {
              setEventTypePrefix(e.target.value);
              setOffset(0);
            }}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
          <datalist id="audit-event-types">
            {(eventTypes.data ?? []).map((et) => (
              <option key={et.event_type} value={et.event_type} />
            ))}
          </datalist>
          {/* C4 -- and a way to SEE them. A datalist only opens if the
              reader already suspects it is there, which is why 69
              recorded types read as none. */}
          {(eventTypes.data?.length ?? 0) > 0 && (
            <select
              data-testid="audit-events-prefix-picker"
              value=""
              onChange={(e) => {
                setEventTypePrefix(e.target.value);
                setOffset(0);
              }}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs text-slate-600"
            >
              <option value="">
                {t("screens.auditEventsView.prefixPick", {
                  count: eventTypes.data?.length ?? 0,
                })}
              </option>
              {(eventTypes.data ?? []).map((et) => (
                <option key={et.event_type} value={et.event_type} title={et.event_type}>
                  {auditEventWords(et.event_type)}
                </option>
              ))}
            </select>
          )}
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">
            {t("screens.auditEventsView.entityType")}
          </span>
          <select
            data-testid="audit-events-filter-entity-type"
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setOffset(0);
            }}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          >
            {ENTITY_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {/* Round 17 H35 -- named, not the bare column value. */}
                {opt
                  ? t(`screens.auditEventsView.kind.${opt}`, { defaultValue: opt })
                  : t("screens.auditEventsView.any")}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">
            {t("screens.auditEventsView.pageSize")}
          </span>
          <select
            data-testid="audit-events-filter-limit"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
          >
            <option value={25}>{25}</option>
            <option value={50}>{50}</option>
            <option value={100}>{100}</option>
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">
            {t("screens.auditEventsView.sinceIso8601", { format: "ISO 8601" })}
          </span>
          <input
            dir="auto"
            data-testid="audit-events-filter-since"
            type="text"
            value={since}
            onChange={(e) => {
              setSince(e.target.value);
              setOffset(0);
            }}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs font-mono"
          />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">
            {t("screens.auditEventsView.untilIso8601", { format: "ISO 8601" })}
          </span>
          <input
            dir="auto"
            data-testid="audit-events-filter-until"
            type="text"
            value={until}
            onChange={(e) => {
              setUntil(e.target.value);
              setOffset(0);
            }}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs font-mono"
          />
        </label>
        <div className="flex items-end gap-2">
          <button
            type="button"
            data-testid="audit-events-refresh"
            onClick={() => void events.refetch()}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700"
          >
            {t("screens.auditEventsView.refresh")}
          </button>
        </div>
      </fieldset>

      {/* Result table */}
      <section
        data-testid="audit-events-results"
        className="rounded border border-slate-200 bg-white"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 text-xs text-slate-600">
          <span data-testid="audit-events-pagination-summary">
            {events.isLoading ? (
              t("screens.auditEventsView.loading")
            ) : (
              /* G3: the digits are isolated so right-to-left cannot
                 reverse them ("1-25 of 2665" rendered backwards), but
                 the connecting word is NOT -- it belongs to the
                 sentence and must flow with the page. */
              <>
                <Figure>
                  {total === 0 ? 0 : offset + 1}-{Math.min(offset + limit, total)}
                </Figure>{" "}
                {t("screens.auditEventsView.rangeOf")} <Figure>{total}</Figure>
              </>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="audit-events-prev"
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={!hasPrev}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 disabled:opacity-50"
            >
              {t("screens.auditEventsView.prev")}
            </button>
            <button
              type="button"
              data-testid="audit-events-next"
              onClick={() => setOffset(offset + limit)}
              disabled={!hasNext}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 disabled:opacity-50"
            >
              {t("screens.auditEventsView.next")}
            </button>
          </div>
        </div>

        {events.error && (
          <p
            data-testid="audit-events-error"
            className="px-3 py-4 text-xs text-rose-700"
          >
            {t("screens.auditEventsView.failedToLoad")}{" "}
            {events.error instanceof Error
              ? events.error.message
              : t("screens.auditEventsView.unknown")}
          </p>
        )}

        {!events.error && rows.length === 0 && !events.isLoading && (
          <p
            data-testid="audit-events-empty"
            className="px-3 py-4 text-xs italic text-slate-500"
          >
            {t("screens.auditEventsView.noAuditEventsMatchTheseFilters")}
          </p>
        )}

        {rows.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-start">
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.auditEventsView.when")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.auditEventsView.actor")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.auditEventsView.eventType")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600">
                  {t("screens.auditEventsView.entity")}
                </th>
                <th className="px-2 py-1 font-semibold text-slate-600"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const target = clickThroughTarget(row);
                const isExpanded = expandedId === row.id;
                return (
                  <RowFragment
                    key={row.id}
                    row={row}
                    target={target}
                    entityLabel={
                      row.entity_label ??
                      (row.entity_id ? entityLabelFromRows(rows, row.entity_id) : null)
                    }
                    isExpanded={isExpanded}
                    onToggleExpand={() => setExpandedId(isExpanded ? null : row.id)}
                    onNavigate={(href) => navigate(href)}
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

interface RowFragmentProps {
  row: AuditEvent;
  target: string | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onNavigate: (href: string) => void;
}

/**
 * The key behind an external request, where there is one.
 *
 * Round 10 F4 -- a refused pull now records the key it could NOT
 * resolve as well, and that row carries a prefix rather than a name.
 * Showing the prefix is right: it is what a reader can match against
 * the register, and it is not a claim that anybody holds it.
 */
function serviceAccountName(row: AuditEvent): string | null {
  if (row.entity_type !== "service_account") return null;
  const payload = (row.payload ?? {}) as Record<string, unknown>;
  const name = payload.service_account;
  if (typeof name === "string" && name) return name;
  const prefix = payload.key_prefix;
  return typeof prefix === "string" && prefix ? `${prefix}\u2026` : null;
}

function RowFragment({
  row,
  target,
  entityLabel,
  isExpanded,
  onToggleExpand,
  onNavigate,
}: RowFragmentProps & { entityLabel?: string | null }) {
  const { t } = useTranslation();
  // Not prose: a truncated identifier. Kept out of JSX so the
  // no-literals rule stays sharp for the prose it exists to catch.
  const shortEntityId = row.entity_id ? `${row.entity_id.slice(0, 8)}\u2026` : "";
  return (
    <>
      <tr
        data-testid="audit-events-row"
        data-event-type={row.event_type}
        data-entity-type={row.entity_type ?? ""}
        className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
        onClick={onToggleExpand}
      >
        <td className="px-2 py-1 font-mono text-slate-700">
          {formatDateTime(row.created_at, i18n.language)}
        </td>
        <td className="px-2 py-1">
          {/* Round 10 F4 -- the one table whose ordering principle is
              who did this, and every outbound pull read "system".
              An external read is made by a KEY, not by a person and
              not by us: the row has carried the key's name in its
              payload since round 9 and the column threw it away. The
              seat is stated too, because a service account is not a
              user and must not read like one. */}
          {row.actor_username ? (
            <span className="text-slate-900">{row.actor_username}</span>
          ) : serviceAccountName(row) ? (
            <span data-testid="audit-actor-service-account" className="text-slate-900">
              {serviceAccountName(row)}{" "}
              <span className="text-slate-500">
                {t("screens.auditEventsView.serviceAccount")}
              </span>
            </span>
          ) : (
            <span className="italic text-slate-500">
              {t("screens.auditEventsView.system")}
            </span>
          )}
        </td>
        <td className="px-2 py-1 text-slate-800">
          <Identifier>{row.event_type}</Identifier>
        </td>
        <td className="px-2 py-1">
          {row.entity_type ? (
            /* C2 -- fifth round. The cell printed a type and a
               truncated uuid, which names nothing a reader recognises;
               the same unisolated string then reordered in
               right-to-left, so the id led its own type.

               The log stores no name, but the rows it returned DO --
               `entityLabelFromRows` reads what the product wrote about
               the subject. Falls back to the short id, which is worse
               to read and still correct. */
            <span className="text-slate-700" dir="auto">
              <Bdi>{entityLabel ?? kindWords(t, row.entity_type)}</Bdi>
              {/* D1 -- the KIND stays beside a resolved name. Showing
                  only the name loses which register the row is in, and
                  showing only the kind was the original defect. */}
              {entityLabel ? (
                <span className="ms-1 rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-600">
                  {kindWords(t, row.entity_type)}
                </span>
              ) : row.entity_id ? (
                <span className="ms-1 font-mono text-slate-500">
                  <Figure>{shortEntityId}</Figure>
                </span>
              ) : null}
            </span>
          ) : (
            <span className="italic text-slate-400">—</span>
          )}
        </td>
        <td className="px-2 py-1">
          {target && (
            <button
              type="button"
              data-testid="audit-events-row-view-source"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(target);
              }}
              className="rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-900"
            >
              {t("screens.auditEventsView.viewSource")}
            </button>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr data-testid="audit-events-row-expanded">
          <td colSpan={5} className="bg-slate-50 px-2 py-2">
            <div className="grid grid-cols-1 gap-2 text-[11px] sm:grid-cols-2">
              <div>
                <span className="block text-slate-500">
                  {t("screens.auditEventsView.ipAddress")}
                </span>
                <span className="font-mono text-slate-800">
                  {row.ip_addr ?? t("screens.auditEventsView.none")}
                </span>
              </div>
              <div>
                <span className="block text-slate-500">
                  {t("screens.auditEventsView.userAgent")}
                </span>
                <span className="font-mono text-slate-800 break-all">
                  {row.user_agent ?? t("screens.auditEventsView.none")}
                </span>
              </div>
            </div>
            <div className="mt-2">
              <span className="block text-[11px] text-slate-500">
                {t("screens.auditEventsView.payload")}
              </span>
              <pre
                data-testid="audit-events-row-payload"
                className="overflow-auto rounded bg-white p-2 text-[10px] font-mono text-slate-800"
              >
                {JSON.stringify(row.payload, null, 2)}
              </pre>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
